<template>
    <div class="smart-product-search-container">
        <FcoTypeahead
            inputClass="fco-typeahead-input"
            :value="inputValue"
            :menu-class="'main-search-results'"
            :results="curResults"
            :resultsUseCategories="isEntSearchEnabled"
            :placeholder="placeholder"
            :map-result="({ displayName }) => displayName"
            :minChars="1"
            mode="custom"
            :maxResults="99"
            :showSearchDuringInput="true"
            :filterInput="cleanTerm"
            :menuOffset="[0, 0]"
            :disableAutoResultSelect="isEntSearchEnabled"
            ref="siteSearch"
            ignore-left-right-arrow-keypress
            @focus="$emit('focus')"
            @search="handleSearch"
            @input-no-debounce="handleActiveInput"
            @enter-press-before-search="quickEntered = true"
            @select="handleSelect"
        />
        <QuoteSelectionModal
            v-if="showNewQuotePrompt"
            @close="handleQuotePromptClose"
            @start-new-quote="startNewQuote(tempVehicle)"
            @use-existing-quote="addToExistingQuote(tempVehicle)"
        />
    </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import { FcoTypeahead } from 'fco/src/vue/components/typeahead';
import axios from 'axios';
import featureFlagsMixin from '@/common/mixins/featureFlagsMixin';
import helpersMixin from '@/common/mixins/helpersMixin';
import { LookupMethod, LookupOrigin, TypeAheadTypes } from '@/common/constants/ordering';
import partTypeSelectionMixin from '@/ordering/partTypeSelection/mixins/partTypeSelectionMixin';
import QuoteSelectionModal from './vehicleSelector/QuoteSelectionModal.vue';
import vsMixin from '../mixins/vsMixin';
import SignalsTrackingMixin from '../../ordering/common/mixins/signalsTrackingMixin';
import { clearAndSelectPartType } from '../services/partTypeService';
import { isRequestSettled } from '../store/request-status';
import analytics, { GTM } from '../../fcoModules/analytics';

// for cancelling axios requests
let source;

export default {
    name: 'ProductSearch',
    mixins: [vsMixin, SignalsTrackingMixin, featureFlagsMixin, helpersMixin, partTypeSelectionMixin],
    data() {
        return {
            curResults: [],
            curPartsResults: [],
            curVehicleResults: [],
            curSearchTerm: '',
            quickEntered: false,
            vehicleRequiredPartsList: [],
            inputValue: '',
            showNewQuotePrompt: false,
            tempVehicle: null,
            keywordRedirect: null,
            partTypeRedirect: null,
            pendingPartType: null,
            searchBasis: null,
        };
    },
    components: { FcoTypeahead, QuoteSelectionModal },
    computed: {
        ...mapState(['requests', 'isSPA', 'currentShop']),
        ...mapState('vehicleSelector', {
            vehicleRequest: (state) => state.requests.getCurrentVehicle,
            currentVehicle: (state) => state.currentVehicle,
            partTypePendingForceCar: (state) => state.partTypePendingForceCar,
        }),
        ...mapState('miniQuote', {
            worksheetId: (state) => state.quoteDetails.worksheetHeaderId,
        }),
        vehicleIsSelected() {
            return !!this.currentVehicle?.vehicleId;
        },
        productsInCurrentQuote() {
            return this.$store.state.miniQuote.quoteDetails.totalItems > 0;
        },
        placeholder() {
            if (!isRequestSettled(this.requests.getFeatures)) return '';
            return this.isEntSearchEnabled ? this.fcoM('keywordsearch.entSearchDefault', 'Enter Product, Part #, or Brand') : this.fcoM('keywordsearch.default', 'Enter Part, Item #, or Brand');
        },
        cleanSearchTerm() {
            return this.cleanTerm(this.curSearchTerm).trim();
        },
    },
    methods: {
        ...mapActions('usageTracking', ['startCatalogLookup']),
        trackSearchData(query = '') {
            this.$store.dispatch('activityTracking/setSearchPerformed', true);
            this.$store.dispatch('activityTracking/setSearchTerm', query);
        },
        startNewQuote(tempVehicle) {
            this.showNewQuotePrompt = false;
            this.handleVehicleSelect(tempVehicle);
        },
        addToExistingQuote(tempVehicle) {
            this.showNewQuotePrompt = false;
            this.handleVehicleSelect(tempVehicle, true);
        },
        handleQuotePromptClose() {
            // reset vehicle prompt data as user has cancelled out of selecting a vehicle
            this.tempVehicle = null;
            this.keywordRedirect = null;
            this.partTypeRedirect = null;
            this.showNewQuotePrompt = false;
        },
        handleActiveInput(term) {
            this.curSearchTerm = term;
        },
        handleSearch() {
            // abort any previous requests if they still exist
            if (source) {
                source.cancel();
            }

            const searchTerm = this.cleanSearchTerm;
            const { CancelToken } = axios;
            let searchUrl = '';
            if (this.isEntSearchEnabled) {
                searchUrl = this.fcoUrl(`/search/keyword/v3/typeahead?query=${encodeURIComponent(searchTerm)}&suggestionType=PART_TYPE`);
            } else {
                searchUrl = this.fcoUrl(`/search/keyword/typeahead?query=${encodeURIComponent(searchTerm)}&entSearchEnabled=false`);
            }
            source = CancelToken.source();

            axios
                .get(searchUrl, { cancelToken: source.token })
                .then(async ({ data }) => {
                    this.curResults = [];
                    this.tempVehicle = null;
                    this.keywordRedirect = null;
                    this.partTypeRedirect = null;
                    this.pendingPartType = null;
                    this.curPartsResults = [...data.partTypes];
                    this.curVehicleResults = [...data.vehicles];

                    const userSearchQueryItem = { displayName: `"${searchTerm}"`, type: TypeAheadTypes.CUSTOM_SEARCH_QUERY };

                    if (this.isEntSearchEnabled) {
                        if (data.partTypes.length) this.curResults = [{ category: this.fcoM('common.partTypes', 'Part Types') }, ...data.partTypes];
                        if (data.vehicles.length)
                            this.curResults = [...this.curResults, { category: this.fcoM('common.vehicles', 'Vehicles') }, ...data.vehicles];
                        this.searchBasis = data.spelling.searchBasis;
                    } else {
                        this.curResults = [...data.partTypes];
                        const searchTermLower = searchTerm.toLowerCase();
                        const hasExactMatch = !!searchTermLower && this.curResults.some((item) => item.displayName.toLowerCase() === searchTermLower);
                        if (!hasExactMatch) {
                            this.curResults.unshift(userSearchQueryItem);
                        }
                    }

                    this.vehicleRequiredPartsList = data.partTypes.filter((part) => part.applicationPartType).map(({ partTypeId }) => partTypeId);

                    // if quickEntered we want to select right away what the user entered as input
                    if (this.quickEntered) {
                        // if we wait for the results list to render then manually triggering handleSelect method will properly blur
                        // and let the typeahead dropdown close on submit
                        await this.$nextTick();
                        await this.startCatalogLookup();
                        this.$store.dispatch('activityTracking/setSearchUuid');
                        this.handleProductSearchSelect(userSearchQueryItem);

                        // need to reset this in case a new search is started and something like forcecar was ignored by the user
                        this.quickEntered = false;
                    }
                })
                .catch(() => {
                    this.curResults = [];
                });
        },
        // Used when a user types a value and presses enter before results appear or if they press enter and their
        // typed result does not exactly match any results.
        async handlePreProcessingRequest(result) {
            const loading = this.$fcoLoading();

            try {
                const {
                    data: { identifiedPartType, itemNumberMatching },
                } = await axios.get(this.fcoUrl(`/search/keyword/preprocess?query=${encodeURIComponent(result)}&shopId=${this.currentShop.id}`));

                if (identifiedPartType) {
                    this.$store.dispatch('activityTracking/setLookupMethod', LookupMethod.PREPROCESSING);
                    this.$store.dispatch('activityTracking/setSearchTerm', result);

                    loading.remove();

                    this.handlePartTypeSelect(identifiedPartType, true);
                    return;
                }

                if (itemNumberMatching) {
                    this.$store.dispatch('activityTracking/setLookupMethod', LookupMethod.ITEM_NUMBER_SEARCH);

                    if (!this.isSPA) {
                        window.location = this.fcoUrl(`/search/product/browse.html?query=${result}&itemNumber=true`);
                        return;
                    }

                    this.$router.push({ path: '/search-keyword', query: { query: result, itemNumber: true } });
                    loading.remove();
                    return;
                }

                this.handleKeywordSearch(result);
                loading.remove();
            } catch {
                this.$fcoToast.error(this.fcoM('rs.getparts.partsRequestError', 'Error retrieving product data. Please try again.'));
                loading.remove();
            }
        },
        // this is when we want to take the string result and redirect to an enterprise search plp
        async handleKeywordSearch(searchQuery) {
            const loading = this.$fcoLoading();

            if (!this.isSPA) {
                window.location = this.fcoUrl(`/search/search.html?query=${encodeURIComponent(searchQuery)}&retain=false`);
                return;
            }

            this.$router.push({ path: '/search-suggestion', query: { query: searchQuery, itemNumber: true } });
            loading.remove();
        },
        // used for typeahead before phase 2 features and for any phase 2 results that are just what the user typed to determine
        // if they match a result or not and how to handle that situation.
        async handleProductSearchSelect(result) {
            const loading = this.$fcoLoading();
            const searchTerm = this.cleanSearchTerm;
            this.$refs.siteSearch.blur();

            this.$store.dispatch('activityTracking/startPartsLookupTimer');

            if (this.isEntSearchEnabled) {
                this.handlePreProcessingRequest(searchTerm);

                this.trackSearchData(result.displayName || result);

                analytics({
                    event: GTM.Action.SEARCH,
                    data: {
                        accountId: this.currentShop.accountNumber,
                        query: result,
                        usedTypeAhead: false,
                    },
                });
                loading.remove();

                return;
            }

            const isPartTypeResult = result.type === TypeAheadTypes.PART_TYPE;
            const isVehicleRequiredForPartType = isPartTypeResult && this.vehicleRequiredPartsList.includes(result.partTypeId);
            const redirect = !this.isSPA
                ? `/search/search.html?query=${encodeURIComponent(isPartTypeResult ? result.displayName : searchTerm)}&retain=false`
                : { path: '/search-suggestion', query: { query: isPartTypeResult ? result.displayName : searchTerm } };

            let analyticsCallback = () => {
                if (!this.isSPA) {
                    window.location = this.fcoUrl(redirect);
                    return;
                }
                this.$router.push(redirect);
                loading.remove();
            };

            if (!this.currentVehicle && isVehicleRequiredForPartType) {
                this.$refs.siteSearch.blur();
                loading.remove();
                this.$store.dispatch('vehicleSelector/forceCar', redirect);

                /*
                 Still send event to analytics that searchbar was used,
                 but let the vehicle selector handle the redirect.
                */
                analyticsCallback = null;
            }

            this.trackSearchData(isPartTypeResult ? result.displayName : searchTerm);

            analytics({
                event: GTM.Action.SEARCH,
                data: {
                    accountId: this.currentShop.accountNumber,
                    query: isPartTypeResult ? result.displayName : searchTerm,
                    usedTypeAhead: isPartTypeResult,
                },
                callback: analyticsCallback,
            });
        },
        // used to select a part type and navigate user to part type plp
        async handlePartTypeSelect(partType, fromPreprocessing = false) {
            const loading = this.$fcoLoading();

            try {
                this.$store.dispatch('activityTracking/startPartsLookupTimer');

                if (partType.applicationPartType && !this.vehicleIsSelected) {
                    // just need to set this to true since we won't actually redirect to anything and still want logic that checks for URL
                    // to open/highlight and do a force car experience
                    this.$refs.siteSearch.blur();
                    this.$store.dispatch('vehicleSelector/forceCar', true);
                    this.$store.dispatch('vehicleSelector/setVsProp', { prop: 'partTypePendingForceCar', value: true });

                    if (this.isEntSearchEnabled) {
                        this.$store.dispatch('vehicleSelector/setVsProp', { prop: 'saveLookupBeforeRedirect', value: true });
                        this.$store.dispatch('vehicleSelector/setVsProp', { prop: 'partsToSaveBeforeRedirect', value: [partType] });
                    }
                    this.pendingPartType = partType;
                    loading.remove();
                    return;
                }

                const partTypes = [
                    {
                        parentId: partType.parentPartTypeId,
                        partTypeId: partType.partTypeId,
                        partTypeName: partType.displayName,
                        platformId: partType.platform,
                        lookupPartTypId: partType.parentPartTypeId,
                        applicationPartType: partType.applicationPartType,
                    },
                ];

                if (partType.siblings) {
                    partType.siblings.forEach((sibling) => {
                        partTypes.push({
                            parentId: sibling.parentPartTypeId,
                            partTypeId: sibling.partTypeId,
                            partTypeName: sibling.displayName,
                            platformId: sibling.platform,
                            lookupPartTypId: sibling.parentPartTypeId,
                            applicationPartType: partType.applicationPartType,
                        });
                    });
                }

                await clearAndSelectPartType(partTypes);

                if (this.isEntSearchEnabled) {
                    // get typeahead part types into a more friendly format for PLP retain and product requests
                    const partsList = partTypes.map((pt) => ({
                        description: pt.description || pt.partTypeName,
                        parentPartTypeId: pt.parentPartTypeId || pt.parentId,
                        partTypeId: pt.partTypeId,
                        platformId: pt.platformId || pt.platform,
                        applicationPartType: pt.applicationPartType,
                    }));

                    if ((!partType.kits && fromPreprocessing && partType.leaf) || (!fromPreprocessing && !partType.kits)) {
                        await this.$_partTypesMixin_getResolvedLeaf(partsList, fromPreprocessing ? LookupOrigin.PREPROCESS : LookupOrigin.TYPEAHEAD);
                    } else {
                        await this.$_partTypeMixin_getResolvedPartTypes(
                            partsList,
                            fromPreprocessing ? LookupOrigin.PREPROCESS : LookupOrigin.TYPEAHEAD
                        );
                    }
                }

                if (!this.isSPA) {
                    window.location.href =
                        !this.isEntSearchEnabled || partType.kits
                            ? this.fcoUrl(`/parttype/mini/getparts.html?query=${encodeURIComponent(partType.displayName)}`)
                            : this.fcoUrl('/parttype/mini/v2/getParts.html');
                    return;
                }

                loading.remove();

                this.$router.push({
                    path: this.getSpaCatalogRedirectPath(Boolean(partType.kits)),
                    query: this.isEntSearchEnabled ? { query: partType.displayName } : undefined,
                });
            } catch {
                this.$fcoToast.error(this.fcoM('rs.getparts.partsRequestError', 'Error retrieving product data. Please try again.'));
                loading.remove();
            }
        },
        // used for handling a result that is selecting a vehicle for the user
        async handleVehicleSelect(baseVehicleId, keepQuote = false) {
            const loading = this.$fcoLoading();
            const vehicleData = {
                baseVehicleId,
                quote: keepQuote,
            };

            const fullVehicleData = this.selectVehicleFromVehicleData(vehicleData, keepQuote);

            // if keywordRedirect has a value we know to do that instead of continue on. This could be from a vehicle
            // prompt or from a preprocessing request that has both a vehicle and keyword search to be handled together.
            if (this.keywordRedirect) {
                await this.$store.commit('vehicleSelector/setCurrentVehicle', fullVehicleData);
                this.handleKeywordSearch(this.keywordRedirect);
                loading.remove();
                return;
            }

            // if partTypeRedirect has a value we know to do that instead of continue on. This could be from a vehicle
            // prompt or from a preprocessing request that has both a vehicle and part type lookup to be handled together.
            if (this.partTypeRedirect) {
                await this.$store.commit('vehicleSelector/setCurrentVehicle', fullVehicleData);
                this.handlePartTypeSelect(this.partTypeRedirect);
                return;
            }

            this.handleVehicleSelectSuccess({ gaEventAction: 'typeahead', vehicle: fullVehicleData });
            if (this.preventNavigationOnVehicleUpdate) {
                if (this.showNewQuotePrompt) this.showNewQuotePrompt = false;
                loading.remove();
            }
        },
        // starting point method for all typeahead selections
        handleSelect(result) {
            this.startCatalogLookup();
            this.$store.dispatch('activityTracking/setSearchUuid');
            // clear out the input field when a selection is made
            this.inputValue = '';
            this.$refs.siteSearch.blur();

            this.$store.dispatch('activityTracking/setLookupMethod', LookupMethod.TYPEAHEAD);
            this.$store.dispatch('activityTracking/setSearchTerm', result.displayName);

            if (this.isEntSearchEnabled) {
                if (result.type === TypeAheadTypes.VEHICLE) {
                    if (this.productsInCurrentQuote) {
                        this.tempVehicle = result.baseVehicleId;
                        this.showNewQuotePrompt = true;
                    } else {
                        this.handleVehicleSelect(result.baseVehicleId);
                    }

                    // track autocomplete signal when a vehicle is manually selected by the user
                    this.trackAutoCompleteSignal(this.curVehicleResults, result);
                } else if (result.type === TypeAheadTypes.PART_TYPE) {
                    this.handlePartTypeSelect(result);

                    this.trackSearchData(result.displayName);

                    analytics({
                        event: GTM.Action.SEARCH,
                        data: {
                            accountId: this.currentShop.accountNumber,
                            query: result.displayName,
                            usedTypeAhead: true,
                        },
                    });

                    // track autocomplete signal when a part type is manually selected by the user
                    this.trackAutoCompleteSignal(this.curPartsResults, result);
                } else {
                    this.handleProductSearchSelect(result);
                }
            } else {
                this.handleProductSearchSelect(result);
            }
        },
        trackAutoCompleteSignal(resultList, { type, displayName, partTypeId }) {
            this.$_signalsMixin_trackAutoCompleteSelection({
                selectedSuggestion: {
                    suggestionType: type,
                    basis: this.searchBasis,
                    partTypeName: displayName,
                    partTypeId,
                },
                position: resultList.findIndex((item) => item.displayName === displayName),
                originalQuery: this.cleanSearchTerm,
            });
        },
        cleanTerm(term) {
            return term.replace(/"|<|>/g, '');
        },
    },
    watch: {
        partTypePendingForceCar(val) {
            // this should only update to false if the vsMixin vehicle select success is called with a pending part type
            if (!val && this.pendingPartType) {
                this.handlePartTypeSelect(this.pendingPartType);
            }
        },
    },
    async mounted() {
        await this.$store.dispatch('requestIfIdle', ['getFeatures', 'getCurrentShop', 'vehicleSelector/getCurrentVehicle']);
    },
};
</script>

<style lang="scss">
@import '~scssVariables/mixins';
@import '~scssVariables/config';

.smart-product-search-container {
    height: 41px;

    .fco-dropdown,
    .fco-dropdown-toggle,
    input {
        height: 100%;
    }
}

.main-search-results {
    a {
        text-decoration: none;
        font-size: 0.875em;
        padding-left: $padding;
    }

    .active {
        background-color: $green;
    }

    .spinner {
        display: none;
    }

    &.fco-dropdown-menu {
        max-height: 1000px;
    }
}
</style>
